import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { Retailer, RetailerRequest, Address } from '../types';

// Convert Firestore timestamp to Date
const toDate = (timestamp: Timestamp | undefined): Date => {
  return timestamp?.toDate() || new Date();
};

// ================== RETAILER REQUESTS ==================

export const getPendingRequestCount = async (): Promise<number> => {
  const q = query(
    collection(db, 'retailerRequests'),
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);
  return snapshot.size;
};

export const getRetailerRequests = async (): Promise<RetailerRequest[]> => {
  const q = query(
    collection(db, 'retailerRequests'),
    orderBy('submittedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      businessName: data.businessName,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone,
      address: data.address as Address,
      taxId: data.taxId,
      businessType: data.businessType,
      status: data.status,
      submittedAt: toDate(data.submittedAt),
      reviewedAt: data.reviewedAt ? toDate(data.reviewedAt) : undefined,
      reviewedBy: data.reviewedBy,
      rejectionReason: data.rejectionReason
    } as RetailerRequest;
  });
};

export const getRetailerRequest = async (id: string): Promise<RetailerRequest | null> => {
  const docRef = doc(db, 'retailerRequests', id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  return {
    id: docSnap.id,
    businessName: data.businessName,
    contactName: data.contactName,
    email: data.email,
    phone: data.phone,
    address: data.address as Address,
    taxId: data.taxId,
    businessType: data.businessType,
    status: data.status,
    submittedAt: toDate(data.submittedAt),
    reviewedAt: data.reviewedAt ? toDate(data.reviewedAt) : undefined,
    reviewedBy: data.reviewedBy,
    rejectionReason: data.rejectionReason
  } as RetailerRequest;
};

export const approveRetailerRequest = async (
  requestId: string,
  adminEmail: string
): Promise<string> => {
  const request = await getRetailerRequest(requestId);
  if (!request) throw new Error('Request not found');

  // Create retailer from request
  const retailerData = {
    businessName: request.businessName,
    contactName: request.contactName,
    email: request.email,
    phone: request.phone,
    address: request.address,
    taxId: request.taxId,
    status: 'active',
    createdAt: serverTimestamp(),
    approvedAt: serverTimestamp(),
    approvedBy: adminEmail,
    totalOrders: 0,
    totalSpent: 0
  };

  const retailerRef = await addDoc(collection(db, 'retailers'), retailerData);

  // Update request status
  await updateDoc(doc(db, 'retailerRequests', requestId), {
    status: 'approved',
    reviewedAt: serverTimestamp(),
    reviewedBy: adminEmail
  });

  return retailerRef.id;
};

export const rejectRetailerRequest = async (
  requestId: string,
  adminEmail: string,
  reason: string
): Promise<void> => {
  await updateDoc(doc(db, 'retailerRequests', requestId), {
    status: 'rejected',
    reviewedAt: serverTimestamp(),
    reviewedBy: adminEmail,
    rejectionReason: reason
  });
};

// Submit new retailer request (from public site)
export const submitRetailerRequest = async (
  data: Omit<RetailerRequest, 'id' | 'status' | 'submittedAt'>
): Promise<string> => {
  const requestData = {
    ...data,
    status: 'pending',
    submittedAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'retailerRequests'), requestData);
  return docRef.id;
};

// ================== RETAILERS ==================

export const getRetailers = async (): Promise<Retailer[]> => {
  const q = query(
    collection(db, 'retailers'),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      businessName: data.businessName,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone,
      address: data.address as Address,
      taxId: data.taxId,
      status: data.status,
      createdAt: toDate(data.createdAt),
      approvedAt: data.approvedAt ? toDate(data.approvedAt) : undefined,
      approvedBy: data.approvedBy,
      totalOrders: data.totalOrders || 0,
      totalSpent: data.totalSpent || 0
    } as Retailer;
  });
};

export const getRetailer = async (id: string): Promise<Retailer | null> => {
  const docRef = doc(db, 'retailers', id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  return {
    id: docSnap.id,
    businessName: data.businessName,
    contactName: data.contactName,
    email: data.email,
    phone: data.phone,
    address: data.address as Address,
    taxId: data.taxId,
    status: data.status,
    createdAt: toDate(data.createdAt),
    approvedAt: data.approvedAt ? toDate(data.approvedAt) : undefined,
    approvedBy: data.approvedBy,
    totalOrders: data.totalOrders || 0,
    totalSpent: data.totalSpent || 0
  } as Retailer;
};

export const updateRetailerStatus = async (
  retailerId: string,
  status: 'active' | 'suspended'
): Promise<void> => {
  await updateDoc(doc(db, 'retailers', retailerId), { status });
};

export const updateRetailer = async (
  retailerId: string,
  data: Partial<Omit<Retailer, 'id' | 'createdAt'>>
): Promise<void> => {
  await updateDoc(doc(db, 'retailers', retailerId), data);
};

// Create retailer directly (for migrating existing customers)
export interface CreateRetailerData {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  address: Address;
  taxId: string;
}

export const createRetailerDirect = async (
  data: CreateRetailerData,
  adminEmail: string
): Promise<string> => {
  const retailerData = {
    businessName: data.businessName,
    contactName: data.contactName,
    email: data.email,
    phone: data.phone,
    address: data.address,
    taxId: data.taxId,
    status: 'active',
    createdAt: serverTimestamp(),
    approvedAt: serverTimestamp(),
    approvedBy: adminEmail,
    totalOrders: 0,
    totalSpent: 0
  };

  const retailerRef = await addDoc(collection(db, 'retailers'), retailerData);
  return retailerRef.id;
};
